import { c as _c } from "react/compiler-runtime";
import React, { useState } from 'react';
import { MdClose } from 'react-icons/md';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslations } from 'next-intl';
import { useContext } from 'use-context-selector';
import { amplitudeInstance } from '../../config/amplitude';
import NativeSelect from '../NativeSelect';
import AppContext from '../../context/AppContext';
import AuthContext from '../../context/AuthContext';
import isStringEmpty from '../../helpers/isStringEmpty';
import api from '../../services/api';
import { UserList } from '../../types/types';
interface PropTypes {
  isOpen: boolean;
  onClose: () => void;
}
function CreateListModal(t0) {
  const $ = _c(103);
  const {
    isOpen,
    onClose
  } = t0;
  const app = useContext(AppContext);
  const queryClient = useQueryClient();
  const auth = React.useContext(AuthContext);
  const t = useTranslations();
  const [listName, setListName] = useState("");
  const [listDescription, setListDescription] = useState("");
  const [selectedPrivacyInput, setSelectedPrivacyInput] = useState("private");
  let t1;
  if ($[0] !== auth || $[1] !== onClose || $[2] !== queryClient) {
    t1 = t2 => {
      const {
        data: data_0
      } = t2;
      if (queryClient.getQueryData([`users-${auth.userData?._id}`])) {
        queryClient.setQueryData([`users-${auth.userData?._id}`], old => ({
          data: {
            ...old.data,
            lists: [data_0, ...old.data.lists]
          }
        }));
      }
      if (queryClient.getQueryData(["me-lists-with-pagination"])) {
        queryClient.setQueryData(["me-lists-with-pagination"], old_0 => {
          const pages = old_0.pages.map(page => ({
            ...page,
            data: [data_0, ...page.data]
          }));
          return {
            pages,
            pageParams: old_0.pageParams
          };
        });
      }
      onClose();
      queryClient.invalidateQueries({
        queryKey: ["me-lists-with-pagination"]
      });
      queryClient.invalidateQueries({
        queryKey: [`users-${auth.userData?._id}`]
      });
      setListName("");
      setListDescription("");
      setSelectedPrivacyInput("private");
    };
    $[0] = auth;
    $[1] = onClose;
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== app || $[5] !== t) {
    t2 = (err, _, rollback) => {
      if (err?.response?.data?.error?.code === "LISTS_LIMIT_REACHED") {
        toast(t("modals.create_list.messages.lists_limit_reached.message", {
          limit: app.clientConfig?.number_of_allowed_lists as number
        }), {
          position: "bottom-center",
          type: "error"
        });
      }
    };
    $[4] = app;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationFn: _temp,
      onSuccess: t1,
      onError: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const createListMutation = useMutation(t3);
  let t4;
  if ($[10] !== onClose) {
    t4 = isOpen_0 => {
      if (!isOpen_0) {
        amplitudeInstance.logEvent("Close Create List Modal");
        onClose();
      }
    };
    $[10] = onClose;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t) {
    t5 = t("modals.create_list.title");
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t5) {
    t6 = <span className="text-white font-bold text-2xl">{t5}</span>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MdClose size={28} />;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onClose) {
    t8 = <button className="text-white" onClick={onClose}>{t7}</button>;
    $[17] = onClose;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t8) {
    t9 = <div className="flex flex-row justify-between items-center">{t6}{t8}</div>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("modals.create_list.inputs.list_name.label");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <label htmlFor="name" className="text-white">{t10}</label>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("modals.create_list.inputs.list_name.placeholder");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e => setListName(e.target.value);
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== listName || $[30] !== t12) {
    t14 = <input className="h-[40px] rounded-md bg-black text-white placeholder:text-gray-200 p-4 mt-2" id="name" placeholder={t12} value={listName} onChange={t13} />;
    $[29] = listName;
    $[30] = t12;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t11 || $[33] !== t14) {
    t15 = <div className="flex flex-col">{t11}{t14}</div>;
    $[32] = t11;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t("modals.create_list.inputs.list_description.label");
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16) {
    t17 = <label htmlFor="name" className="text-white">{t16}</label>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("modals.create_list.inputs.list_description.placeholder");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e_0 => setListDescription(e_0.target.value);
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== listDescription || $[43] !== t18) {
    t20 = <input className="h-[40px] rounded-md bg-black text-white placeholder:text-gray-200 p-4 mt-2" id="name" placeholder={t18} value={listDescription} onChange={t19} />;
    $[42] = listDescription;
    $[43] = t18;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t17 || $[46] !== t20) {
    t21 = <div className="flex flex-col mt-4">{t17}{t20}</div>;
    $[45] = t17;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("modals.create_list.inputs.privacy.label");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t22) {
    t23 = <label htmlFor="privacy" className="text-white">{t22}</label>;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_1 => setSelectedPrivacyInput(e_1.target.value);
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t) {
    t25 = t("user_list_privacy.public");
    $[53] = t;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t25) {
    t26 = <option value="public">{t25}</option>;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t) {
    t27 = t("user_list_privacy.private");
    $[57] = t;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t27) {
    t28 = <option value="private">{t27}</option>;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== selectedPrivacyInput || $[62] !== t26 || $[63] !== t28) {
    t29 = <NativeSelect id="privacy" containerClassName="mt-2" className="h-[40px] rounded-md border-[1px] border-gray-200 bg-black px-4 text-white" value={selectedPrivacyInput} onChange={t24}>{t26}{t28}</NativeSelect>;
    $[61] = selectedPrivacyInput;
    $[62] = t26;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t23 || $[66] !== t29) {
    t30 = <div className="flex flex-col mt-4">{t23}{t29}</div>;
    $[65] = t23;
    $[66] = t29;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== t15 || $[69] !== t21 || $[70] !== t30) {
    t31 = <div className="flex flex-col mt-4">{t15}{t21}{t30}</div>;
    $[68] = t15;
    $[69] = t21;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== onClose) {
    t32 = () => {
      setListName("");
      setSelectedPrivacyInput("private");
      amplitudeInstance.logEvent("Close Create List Modal");
      onClose();
    };
    $[72] = onClose;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t) {
    t33 = t("modals.create_list.cancel");
    $[74] = t;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t32 || $[77] !== t33) {
    t34 = <button onClick={t32} className="h-[40px] bg-white hover:bg-gray-100 text-black rounded-md px-4 font-bold mr-2">{t33}</button>;
    $[76] = t32;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== createListMutation || $[80] !== listDescription || $[81] !== listName || $[82] !== selectedPrivacyInput) {
    t35 = () => {
      if (!createListMutation.isPending) {
        amplitudeInstance.logEvent("Create List", {
          list_privacy: selectedPrivacyInput
        });
        createListMutation.mutate({
          data: {
            name: listName,
            description: listDescription,
            privacy: selectedPrivacyInput
          }
        });
      }
    };
    $[79] = createListMutation;
    $[80] = listDescription;
    $[81] = listName;
    $[82] = selectedPrivacyInput;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t) {
    t36 = t("modals.create_list.save");
    $[84] = t;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== createListMutation.isPending) {
    t37 = createListMutation.isPending && <ClipLoader className="ml-2" color="white" size={18} />;
    $[86] = createListMutation.isPending;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] !== t35 || $[89] !== t36 || $[90] !== t37) {
    t38 = <button onClick={t35} className="flex flex-row items-center justify-center h-[40px] bg-red-600 hover:bg-red-500 text-white rounded-md px-4 font-bold">{t36}{t37}</button>;
    $[88] = t35;
    $[89] = t36;
    $[90] = t37;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== t34 || $[93] !== t38) {
    t39 = <div className="flex flex-row justify-end mt-4">{t34}{t38}</div>;
    $[92] = t34;
    $[93] = t38;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  let t40;
  if ($[95] !== t31 || $[96] !== t39 || $[97] !== t9) {
    t40 = <Dialog.Portal><Dialog.Overlay className="flex justify-center items-center fixed inset-0 bg-[rgba(0,0,0,0.7)] bg-opacity-25 transition-opacity z-10 px-2"><Dialog.Content className="w-full md:w-[500px] bg-gray-600 rounded-md p-4">{t9}{t31}{t39}</Dialog.Content></Dialog.Overlay></Dialog.Portal>;
    $[95] = t31;
    $[96] = t39;
    $[97] = t9;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== isOpen || $[100] !== t4 || $[101] !== t40) {
    t41 = <Dialog.Root open={isOpen} onOpenChange={t4}>{t40}</Dialog.Root>;
    $[99] = isOpen;
    $[100] = t4;
    $[101] = t40;
    $[102] = t41;
  } else {
    t41 = $[102];
  }
  return t41;
}
async function _temp(t0) {
  const {
    data
  } = t0;
  const response = await api.post("/v1/me/lists", {
    name: data.name,
    description: !isStringEmpty(data.description) ? data.description : null,
    privacy: data.privacy
  });
  return response.data;
}
export default CreateListModal;